import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { money, PLACEHOLDER } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useEffect, useCallback } from "react";
import { Star, MapPin, Headphones, Loader2, Minus, Plus, CalendarDays, Send, MessageSquare, ChevronLeft, ChevronRight, ImagePlus } from "lucide-react";

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function StudioDetailDialog({ studio, open, onClose, onRequireAuth }) {
  const { t } = useLang();
  const { user } = useAuth();
  const kycOk = user?.kyc_status === "verified";
  const navigate = useNavigate();
  const [date, setDate] = useState(todayStr());
  const [avail, setAvail] = useState(null);
  const [start, setStart] = useState(null);
  const [hours, setHours] = useState(1);
  const [engineer, setEngineer] = useState(false);
  const [payMethod, setPayMethod] = useState("card");
  const [busy, setBusy] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [myPhoto, setMyPhoto] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);

  const loadAvail = useCallback(async () => {
    setStart(null);
    const { data } = await api.get(`/studios/${studio.id}/availability`, { params: { date } });
    setAvail(data);
  }, [studio.id, date]);

  const loadReviews = useCallback(async () => {
    const { data } = await api.get(`/studios/${studio.id}/reviews`);
    setReviews(data.reviews);
  }, [studio.id]);

  useEffect(() => { if (open) { loadAvail(); loadReviews(); setPhotoIdx(0); } }, [open, loadAvail, loadReviews]);

  const slots = avail
    ? Array.from({ length: avail.close_hour - avail.open_hour }, (_, i) => avail.open_hour + i)
    : [];
  const booked = new Set(avail?.booked_hours || []);

  const rangeValid = (() => {
    if (start == null) return false;
    for (let h = start; h < start + hours; h++) {
      if (h >= (avail?.close_hour ?? 24) || booked.has(h)) return false;
    }
    return true;
  })();

  const engRate = studio.engineer_available ? studio.engineer_price_per_hour : 0;
  const studioTotal = studio.price_per_hour * hours;
  const engTotal = engineer ? engRate * hours : 0;
  const cardFee = payMethod === "card" ? 0.99 : 0;
  const total = studioTotal + engTotal + cardFee;

  const payAndBook = async () => {
    if (!rangeValid) return;
    if (!user) { onRequireAuth?.(); return; }
    if (!kycOk) { toast.error(t("kyc_required")); return; }
    setBusy(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        studio_id: studio.id, date, start_hour: start, hours, want_engineer: engineer, payment_method: payMethod,
      });
      if (payMethod === "cash") {
        toast.success(t("booking_confirmed"));
        onClose();
        return;
      }
      const { data } = await api.post("/payments/checkout", {
        booking_id: booking.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail === "KYC_REQUIRED" ? t("kyc_required") : (e?.response?.data?.detail || "Error"));
      setBusy(false);
    }
  };

  const uploadReviewPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMyPhoto(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    } catch { toast.error("Error"); }
  };

  const submitReview = async () => {
    if (!myRating) return;
    setSavingReview(true);
    try {
      await api.post(`/studios/${studio.id}/reviews`, { rating: myRating, comment: myComment, photo: myPhoto });
      toast.success(t("review_added"));
      setMyComment(""); setMyRating(0); setMyPhoto("");
      loadReviews();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? t("only_booked_review") : (e?.response?.data?.detail || "Error"));
    } finally {
      setSavingReview(false);
    }
  };

  const isRec = studio.type === "recording";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="studio-detail">
        <div className="relative h-52 shrink-0">
          <img src={(studio.photos && studio.photos[photoIdx]) || PLACEHOLDER} alt={studio.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
          {studio.photos && studio.photos.length > 1 && (
            <>
              <button data-testid="gallery-prev" onClick={() => setPhotoIdx((i) => (i - 1 + studio.photos.length) % studio.photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"><ChevronLeft className="w-5 h-5" /></button>
              <button data-testid="gallery-next" onClick={() => setPhotoIdx((i) => (i + 1) % studio.photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"><ChevronRight className="w-5 h-5" /></button>
              <div className="absolute top-2 right-3 flex gap-1">
                {studio.photos.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === photoIdx ? "bg-white" : "bg-white/40"}`} />)}
              </div>
            </>
          )}
          <div className="absolute bottom-3 left-4 right-4 flex gap-2">
            <Badge className={`${isRec ? "bg-blue-500/90" : "bg-pink-500/90"} text-white border-0`}>{isRec ? t("recording") : t("rehearsal")}</Badge>
            {studio.engineer_available && <Badge className="bg-emerald-500/90 text-white border-0 gap-1"><Headphones className="w-3 h-3" /> {t("with_engineer")}</Badge>}
          </div>
        </div>

        <div className="p-5">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{studio.name}</DialogTitle>
            <DialogDescription className="sr-only">{studio.city} · {t("book_now")}</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
            <MapPin className="w-3.5 h-3.5" /> {studio.address ? `${studio.address}, ` : ""}{studio.city}
            {studio.rating > 0 && <span className="flex items-center gap-1 text-accent"><Star className="w-3 h-3 fill-accent" /> {studio.rating}</span>}
          </p>

          {studio.description && <p className="text-sm text-muted-foreground mt-3">{studio.description}</p>}

          {studio.amenities?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {studio.amenities.map((a) => (
                <span key={a} className="text-[11px] px-2 py-1 rounded-md bg-secondary text-muted-foreground">{a}</span>
              ))}
            </div>
          )}

          {/* Date */}
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-5 mb-1">{t("select_date")}</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="booking-date"
                className="w-full h-11 rounded-xl bg-secondary/60 border border-border px-3 text-sm flex items-center gap-2 text-left hover:border-primary/60 transition-colors"
              >
                <CalendarDays className="w-4 h-4 text-primary" />
                {new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "long" })}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={new Date(date + "T00:00:00")}
                onSelect={(d) => d && setDate(fmtDate(d))}
                disabled={(d) => d < new Date(new Date().toDateString())}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* Slots */}
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-4 mb-2">{t("select_time")}</label>
          <div className="grid grid-cols-4 gap-2">
            {slots.map((h) => {
              const isBooked = booked.has(h);
              const selected = start != null && h >= start && h < start + hours;
              return (
                <button
                  key={h}
                  data-testid={`slot-${h}`}
                  disabled={isBooked}
                  onClick={() => setStart(h)}
                  className={`slot-btn h-10 rounded-lg text-xs font-semibold border ${
                    isBooked ? "opacity-30 cursor-not-allowed border-border bg-secondary/40"
                    : selected ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-secondary/60 hover:border-primary/60"
                  }`}
                >
                  {String(h).padStart(2, "0")}:00
                </button>
              );
            })}
          </div>

          {/* Hours stepper */}
          <div className="flex items-center justify-between mt-4 p-3 rounded-xl bg-secondary/40 border border-border">
            <span className="text-sm font-medium">{hours} {hours === 1 ? t("hour") : t("hours")}</span>
            <div className="flex items-center gap-2">
              <button data-testid="hours-minus" onClick={() => setHours((h) => Math.max(1, h - 1))} className="w-8 h-8 rounded-lg bg-secondary border border-border flex items-center justify-center"><Minus className="w-4 h-4" /></button>
              <button data-testid="hours-plus" onClick={() => setHours((h) => Math.min(8, h + 1))} className="w-8 h-8 rounded-lg bg-secondary border border-border flex items-center justify-center"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Engineer add-on */}
          {studio.engineer_available && (
            <div className="mt-3 p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Headphones className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-sm font-medium">{t("engineer_addon")}</p>
                    <p className="text-xs text-muted-foreground">{money(engRate, studio.currency)}{t("per_hour")} · {t("engineer_desc")}</p>
                  </div>
                </div>
                <Switch data-testid="engineer-toggle" checked={engineer} onCheckedChange={setEngineer} />
              </div>
              {(studio.engineer_photo || studio.engineer_bio) && (
                <div className="flex gap-3 mt-3 pt-3 border-t border-emerald-500/20">
                  {studio.engineer_photo && <img src={studio.engineer_photo} alt="engineer" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                  {studio.engineer_bio && <p className="text-xs text-muted-foreground flex-1">{studio.engineer_bio}</p>}
                </div>
              )}
            </div>
          )}

          {/* Payment method */}
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-4 mb-2">{t("payment_method")}</label>
          <div className="grid grid-cols-2 gap-2">
            {[{ id: "card", label: t("pay_card") }, { id: "cash", label: t("pay_cash") }].map((m) => (
              <button key={m.id} type="button" data-testid={`pay-method-${m.id}`} onClick={() => setPayMethod(m.id)}
                className={`h-11 rounded-xl border text-sm font-medium transition-all ${payMethod === m.id ? "border-primary bg-primary/15" : "border-border bg-secondary/40 text-muted-foreground"}`}>
                {m.label}
              </button>
            ))}
          </div>
          {payMethod === "cash" && <p className="text-xs text-muted-foreground mt-2">{t("cash_note")}</p>}

          {/* Summary */}
          <div className="mt-4 p-4 rounded-xl bg-secondary/50 border border-border space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("booking_summary")}</p>
            <div className="flex justify-between text-sm"><span>{t("studio_time")} ({hours}h)</span><span>{money(studioTotal, studio.currency)}</span></div>
            {engineer && <div className="flex justify-between text-sm text-emerald-400"><span>{t("engineer_fee")} ({hours}h)</span><span>{money(engTotal, studio.currency)}</span></div>}
            {payMethod === "card" && <div className="flex justify-between text-sm text-muted-foreground"><span>{t("card_fee")}</span><span>{money(cardFee, studio.currency)}</span></div>}
            <div className="flex justify-between font-display font-bold text-lg pt-1 border-t border-border"><span>{t("total")}</span><span className="text-primary">{money(total, studio.currency)}</span></div>
          </div>

          <Button
            data-testid="pay-book-btn"
            onClick={payAndBook}
            disabled={!rangeValid || busy || (!!user && !kycOk)}
            className="w-full h-12 mt-4 rounded-xl bg-primary hover:bg-primary/90 font-semibold text-base"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("processing")}</> : `${t("pay_book")} · ${money(total, studio.currency)}`}
          </Button>
          {!user ? <p data-testid="auth-note" className="text-xs text-primary text-center mt-2">{t("signup_to_book")}</p> : (!kycOk && <p data-testid="kyc-warning" className="text-xs text-amber-400 text-center mt-2">{t("kyc_required")}</p>)}

          {/* Reviews */}
          <div className="mt-6 pt-5 border-t border-border">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-primary" />
              <h3 className="font-display font-semibold">{t("reviews")} ({reviews.length})</h3>
            </div>

            <div className="p-3 rounded-xl bg-secondary/40 border border-border mb-3">
              <p className="text-xs text-muted-foreground mb-2">{t("your_rating")}</p>
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`review-star-${n}`} onClick={() => setMyRating(n)} type="button">
                    <Star className={`w-6 h-6 transition-colors ${n <= myRating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                  </button>
                ))}
              </div>
              <Textarea data-testid="review-comment" value={myComment} onChange={(e) => setMyComment(e.target.value)} placeholder={t("write_review")} rows={2} className="bg-secondary/60 mb-2" />
              <div className="flex items-center gap-2 mb-2">
                {myPhoto && <img src={myPhoto} alt="" className="w-12 h-12 rounded-lg object-cover border border-border" />}
                <label data-testid="review-photo-label" className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border bg-secondary/60 text-xs cursor-pointer hover:bg-secondary">
                  <ImagePlus className="w-3.5 h-3.5" /> {t("photos")}
                  <input data-testid="review-photo-input" type="file" accept="image/*" onChange={uploadReviewPhoto} className="hidden" />
                </label>
              </div>
              <Button data-testid="submit-review-btn" onClick={submitReview} disabled={!myRating || savingReview} size="sm" className="rounded-lg bg-primary hover:bg-primary/90">
                {savingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> {t("submit_review")}</>}
              </Button>
            </div>

            {reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("no_reviews")}</p>
            ) : (
              <div className="space-y-2">
                {reviews.map((r) => (
                  <div key={r.id} className="p-3 rounded-xl border border-border bg-card">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {r.user_picture && <img src={r.user_picture} alt="" className="w-7 h-7 rounded-full object-cover" />}
                        <div className="min-w-0">
                          <span className="text-sm font-medium block truncate">{r.artist_name || r.user_name}</span>
                          {r.bio && <span className="text-[11px] text-muted-foreground block truncate">{r.bio}</span>}
                        </div>
                      </div>
                      <span className="flex items-center gap-0.5 text-accent text-sm shrink-0">
                        {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3 h-3 fill-accent" />)}
                      </span>
                    </div>
                    {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
                    {r.photo && <img src={r.photo} alt="room" className="mt-2 w-full max-h-40 object-cover rounded-lg border border-border" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
